import type { Distance, Point } from "@pdfking/math"
import { Abs, Max } from "@pdfking/math"
import type { Matrix, PathPoint, PlotShape } from "@pdfking/renderer-api"
import { MatrixApply, MatrixCompose, MatrixIdentity, PlotFunction } from "@pdfking/renderer-api"

import type { GrowSize } from "#internal"
import { CUT_LAYER_ID } from "#internal"

import { DistanceRound } from "../../../utils/DistanceRound"
import { CreateExtraShapes } from "./CreateExtraShapes"

// Steps a curved segment of the die is drawn in
const CURVE_STEPS = 16

// The closed paths among `shapes`, their points through `matrix` and every
// group's on the way down
const closed_paths = (shapes: PlotShape[], matrix: Matrix<Distance>): PathPoint[][] =>
  shapes.flatMap(shape => {
    if (shape.enum === PlotFunction.PLOT_GROUP)
      return closed_paths(shape.shapes, MatrixCompose(shape.matrix, matrix))
    if (shape.enum !== PlotFunction.PLOT_PATH || !shape.closed) return []
    const to = (point: Point<Distance>) => MatrixApply(matrix, point)
    return [
      shape.points.map(point => ({
        ...to(point),
        before: to(point.before),
        after: to(point.after),
      })),
    ]
  })

const same = (a: Point<Distance>, b: Point<Distance>) => a.x === b.x && a.y === b.y

// The path as a polygon: a straight segment its start, a curve its start and
// the steps along it
const polygon = (path: PathPoint[]): Point<Distance>[] =>
  path.flatMap((from, i) => {
    const to = path[(i + 1) % path.length]!
    if (same(from.after, from) && same(to.before, to)) return [{ x: from.x, y: from.y }]
    return Array.from({ length: CURVE_STEPS }, (_, k) => {
      const t = k / CURVE_STEPS
      const u = 1 - t
      const at = (p: number, c1: number, c2: number, q: number) =>
        (u * (u * (u * p + 3 * t * c1) + 3 * t ** 2 * c2) + t ** 3 * q) as Distance
      return {
        x: at(from.x, from.after.x, to.before.x, to.x),
        y: at(from.y, from.after.y, to.before.y, to.y),
      }
    })
  })

const area = (points: Point<Distance>[]): number =>
  Abs(
    points.reduce((sum, { x, y }, i) => {
      const next = points[(i + 1) % points.length]!
      return sum + x * next.y - next.x * y
    }, 0) / 2
  )

/** The outline a grow's die cuts it to, in the grow's own frame, y up, as
 * `CreateExtraShapes` draws it: the largest closed cut, its curves in steps,
 * and every other closed cut, the `holes` within it — a handle's slot; none for a grow with
 * no die, or one drawing no closed cut. */
export const GrowOutline = (
  grow_id: string,
  size: GrowSize,
  opening_top: boolean
): { outline: Point<Distance>[]; holes: Point<Distance>[][] } => {
  const cut = (CreateExtraShapes(grow_id, size, opening_top)[CUT_LAYER_ID] ?? []) as PlotShape[]
  // To the spec's precision: arcs and curves land on the same µm on any engine
  const polygons = closed_paths(cut, MatrixIdentity()).map(path =>
    polygon(path).map(({ x, y }) => ({ x: DistanceRound(x), y: DistanceRound(y) }))
  )
  if (!polygons.length) return { outline: [], holes: [] }
  const largest = Max(...polygons.map(area))
  const outline = polygons.find(points => area(points) === largest)!
  return { outline, holes: polygons.filter(points => points !== outline) }
}
